import React, { useContext, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Button, InputLabel, Stack, TextField } from '@mui/material';

import { DraftManualCRQContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import useManageCRQ from 'views/App/Portal/Policy/DraftManuals/_data_sources/crq/useManageCRQ';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { CRQChoice, CRQuestion } from 'views/App/Portal/Policy/_types/crq';
import { ManualContent } from 'views/App/Portal/Policy/_types/manuals/draft';
import ChoicesInput from '../ChoicesInput';
import draftManualHelper from '../../helpers/draftManualHelper';

export type NewQuestionProps = {
  setLastQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
};

const NewQuestion: React.FC<NewQuestionProps> = ({ setLastQuestionId }: NewQuestionProps) => {
  const queryClient = useQueryClient();
  const { manual } = useContext(DraftManualCRQContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);

  const { create: createQuestion } = useManageCRQ();

  const crqQuestionSchema = Yup.object().shape({
    choices: Yup.array()
      .transform((value, originalValue) => {
        return originalValue.filter((x: CRQChoice) => x.value.length > 0);
      })
      .min(2, 'At least 2 choices are required for a question'),
    question: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('draftManuals.crq.properties.question')} is required`),
    hint: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      }),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      choices: [
        { value: 'Option 1', content: [] },
        { value: 'Option 2', content: [] },
      ],
      question: '',
      hint: '',
    },
    resolver: yupResolver(crqQuestionSchema),
  });

  const handleSave: SubmitHandler<CRQuestion> = (values: CRQuestion) => {
    setIsSaving(true);
    console.log('values', values);
    createQuestion(
      manual.id,
      values,
      (data) => {
        reset();
        queryClient.invalidateQueries(['draft_manuals_crq']);
        if (data.id) setLastQuestionId(data.id);

        setIsSaving(false);
        setShowNewModal(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  return (
    <>
      <Button
        variant='contained'
        color='secondary'
        onClick={() => setShowNewModal(true)}
        size='small'
      >
        {t('draftManuals.crq.actions.create')}
      </Button>
      <Modal
        title={t('draftManuals.crq.new.title')}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <>
            <Button
              color='neutral'
              onClick={() => setShowNewModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                trigger();
              }}
              disabled={isSaving}
              component='button'
              type='submit'
              variant='contained'
            >
              {t('common.save')}
            </Button>
          </>
        }
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
      >
        <Alert severity='info'>{t('draftManuals.crq.properties.minChoices')}</Alert>
        {errorHelper.renderAllErrors(serverErrors, errors)}

        <TextField
          id='question'
          key='question'
          label={t('draftManuals.crq.properties.question')}
          variant='outlined'
          required
          fullWidth
          {...register('question')}
          error={!!errors.question}
          helperText={errors.question ? errors.question.message : undefined}
        />
        <TextField
          id='hint'
          key='hint'
          label={t('draftManuals.crq.properties.hint')}
          variant='outlined'
          fullWidth
          {...register('hint')}
          error={!!errors.hint}
          helperText={errors.hint ? errors.hint.message : undefined}
        />
        <Stack
          spacing={0}
          key='choices'
        >
          <InputLabel>{t('draftManuals.crq.properties.choices')}</InputLabel>
          <Controller
            name='choices'
            control={control}
            render={({ field }) => (
              <ChoicesInput
                id='choices'
                manualContent={draftManualHelper.getManualContent(manual)}
                {...field}
              />
            )}
          />
        </Stack>
      </Modal>
    </>
  );
};

export default NewQuestion;
